/**
 * Platform Dashboard - Overview
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, DollarSign, TrendingUp, ExternalLink } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.mango-business.solutions/api/v1';

interface Stats {
  totalBusinesses: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  newBusinessesThisMonth: number;
}

interface RecentBusiness {
  id: string;
  name: string;
  email: string;
  planType: string;
  createdAt: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalBusinesses: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    newBusinessesThisMonth: 0,
  });
  const [recentBusinesses, setRecentBusinesses] = useState<RecentBusiness[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('platformAdminToken');
      
      // Load businesses
      const response = await fetch(`${API_BASE_URL}/platform/businesses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const businesses = data.businesses || [];
        
        setStats({
          totalBusinesses: businesses.length,
          activeSubscriptions: businesses.filter((b: any) => b.subscriptionStatus === 'active').length,
          monthlyRevenue: businesses.reduce((acc: number, b: any) => acc + (b.monthlyFee || 0), 0),
          newBusinessesThisMonth: businesses.filter((b: any) => {
            const createdAt = new Date(b.createdAt);
            const now = new Date();
            return createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear();
          }).length,
        });
        
        setRecentBusinesses(businesses.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Building2, label: 'Total Businesses', value: stats.totalBusinesses, color: '#FFA500' },
    { icon: Users, label: 'Active Subscriptions', value: stats.activeSubscriptions, color: '#22C55E' },
    { icon: DollarSign, label: 'Monthly Revenue', value: `$${stats.monthlyRevenue.toFixed(2)}`, color: '#3B82F6' },
    { icon: TrendingUp, label: 'New This Month', value: stats.newBusinessesThisMonth, color: '#8B5CF6' },
  ];

  if (loading) {
    return <div style={{ color: '#9ca3af' }}>Loading dashboard...</div>;
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Platform Dashboard</h1>
        <p style={styles.subtitle}>Overview of Mangoes Unified platform</p>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        {statCards.map((stat, index) => (
          <div key={index} style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: `${stat.color}20`, color: stat.color }}>
              <stat.icon size={24} />
            </div>
            <div style={styles.statValue}>{stat.value}</div>
            <div style={styles.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Quick Actions</h2>
        <div style={styles.actionsGrid}>
          <Link to="/businesses" style={styles.actionCard}>
            <Building2 size={24} />
            <span>View All Businesses</span>
          </Link>
          <Link to="/subscriptions" style={styles.actionCard}>
            <DollarSign size={24} />
            <span>Manage Subscriptions</span>
          </Link>
          <Link to="/support" style={styles.actionCard}>
            <Users size={24} />
            <span>Customer Support</span>
          </Link>
        </div>
      </div>

      {/* Recent Businesses */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Recent Businesses</h2>
          <Link to="/businesses" style={styles.viewAll}>View All →</Link>
        </div>
        
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Business</th>
                <th style={styles.th}>Plan</th>
                <th style={styles.th}>Joined</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentBusinesses.map((business) => (
                <tr key={business.id}>
                  <td style={styles.td}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{business.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>{business.email}</div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      background: business.planType === 'enterprise' ? 'rgba(139, 92, 246, 0.2)' : 
                                 business.planType === 'growth' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)',
                      color: business.planType === 'enterprise' ? '#8B5CF6' :
                             business.planType === 'growth' ? '#22C55E' : '#9ca3af',
                    }}>
                      {business.planType}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {new Date(business.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    <Link to={`/businesses/${business.id}`} style={styles.link}>
                      <ExternalLink size={16} /> View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    color: '#9ca3af',
    marginTop: '5px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    background: '#1a1a2e',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #252540',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '15px',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#fff',
  },
  statLabel: {
    color: '#9ca3af',
    fontSize: '0.9rem',
  },
  section: {
    marginBottom: '30px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#fff',
    margin: 0,
  },
  viewAll: {
    color: '#FFA500',
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '15px',
  },
  actionCard: {
    background: '#1a1a2e',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #252540',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#fff',
    textDecoration: 'none',
    transition: 'border-color 0.2s',
  },
  tableCard: {
    background: '#1a1a2e',
    borderRadius: '12px',
    border: '1px solid #252540',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '16px 20px',
    color: '#9ca3af',
    fontWeight: 500,
    fontSize: '0.85rem',
    borderBottom: '1px solid #252540',
  },
  td: {
    padding: '16px 20px',
    borderBottom: '1px solid #252540',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#FFA500',
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
};

export default Dashboard;
